import React from "react";
import { SignalDef } from "../models/s7DbMap";

/**
 * Per-tile content layout per EDGE_LIVE_MONITOR_SPEC.md §3.2.2:
 *
 *   ┌──────────────────────┐
 *   │ DOOR_CLOSED   I 34.0 │   line 1: Mono symbol + Mono PLC address (right-aligned, dim)
 *   │ ●   open             │   line 2: dot (12px, semantic colour) + decoded value text
 *   │ Door close (Trens)   │   line 3: Slovak+English label (Inter 12px, dim)
 *   └──────────────────────┘
 *
 * Numeric tiles replace line 2 with right-aligned Mono 18px value + caption.
 * Placeholder tiles render with dashed border + "—" + line-3 hint per §3.2.3.
 *
 * Color rules per §3.2.3 + §5.2:
 *   - good-when-true + value true  → green dot, green-tinted top stripe, bold label
 *   - bad-when-true  + value true  → terracotta dot, terracotta top stripe, bold label
 *   - bad-when-false + value false → terracotta dot, terracotta top stripe, bold label
 *   - bad-when-false + value true  → green dot (since post-invert TRUE = OK)
 *   - mode flag with value true (and isActiveMode=true) → sage tint + bolder label
 *   - info bool                    → sage/grey dot per truthiness, neutral tile
 *   - number                       → right-aligned Mono 18px value + caption
 *   - null/undefined               → dashed border + "—" centred + dimmed
 *   - stale (>10s no update)       → 40% opacity + "stale" badge top-right
 */

const COLORS = {
  green: "#2D8C4E", // good
  greenTintBg: "#E8EFE6", // operational state / safety-OK / chuck section
  greenStripe: "#2D8C4E",
  blueGreyDot: "#3A6FA8", // informational-true
  blueGreyTintBg: "#E5ECEF", // coolant section
  cream: "#F0EBDF", // cycle/production section
  neutral: "#F4F2EC", // process variables section
  terracotta: "#D4572B", // bad
  terracottaTintBg: "#FCEBE3",
  terracottaStripe: "#D4572B",
  amber: "#D4B040", // stale badge
  greyDot: "#8C9099",
  dimText: "#6B6B6B",
  primaryText: "#1A1A1A",
  border: "#E5E2D9",
  dashedBorder: "#C9C5BB",
  white: "#FFFFFF",
};

const FONT_SORA = "'Sora', system-ui, -apple-system, sans-serif";
const FONT_MONO = "'JetBrains Mono', 'SF Mono', Menlo, Consolas, monospace";
const FONT_INTER = "'Inter', system-ui, -apple-system, sans-serif";

type TileBgStyle = "operational" | "safety" | "cycle" | "process" | "chuck" | "coolant" | "alarms";

export interface SignalTileProps {
  signal: SignalDef;
  /**
   * Raw value from MachineStatusDto.signals[key]. May be undefined if §13.2
   * not yet deployed (then the tile renders as null/empty).
   */
  value: unknown;
  /** Whether the tile has not received a fresh value within the stale threshold. */
  stale: boolean;
  /** True if the tile is the currently-active mode flag (only one of 4). */
  isActiveMode?: boolean;
  /** Which section's background tint to apply. */
  bgStyle: TileBgStyle;
}

const sectionBg = (bg: TileBgStyle): string => {
  switch (bg) {
    case "operational":
      return COLORS.white;
    case "safety":
      return COLORS.white;
    case "cycle":
      return COLORS.cream;
    case "process":
      return COLORS.neutral;
    case "chuck":
      return COLORS.cream;
    case "coolant":
      return COLORS.blueGreyTintBg;
    case "alarms":
      return COLORS.white;
    default:
      return COLORS.white;
  }
};

/** Decoded value text for line 2 of a bool tile. */
const formatBoolValue = (v: unknown): string => {
  if (v === true) return "true";
  if (v === false) return "false";
  return "—";
};

/** Decoded value text for line 2 of a numeric tile (returns formatted + unit slot). */
const formatNumber = (v: unknown): string => {
  if (typeof v === "number" && Number.isFinite(v)) {
    // REAL → 1 decimal; INT → integer. Heuristic: integer if Math.floor === v.
    if (Math.abs(v - Math.floor(v)) < 1e-9) return String(Math.floor(v));
    return v.toFixed(1);
  }
  return "—";
};

const SignalTile: React.FC<SignalTileProps> = ({
  signal,
  value,
  stale,
  isActiveMode = false,
  bgStyle,
}) => {
  const isPlaceholder = signal.kind === "placeholder";
  const isUnknown = !isPlaceholder && (value === null || value === undefined);

  // Decide the tile state colour.
  let dotColor = COLORS.greyDot;
  let topStripe: string | null = null;
  let bgColor = sectionBg(bgStyle);
  let labelBold = false;

  if (signal.kind === "bool" && !isUnknown) {
    const truthy = value === true;
    switch (signal.semantic) {
      case "good-when-true":
        if (truthy) {
          dotColor = COLORS.green;
          topStripe = COLORS.greenStripe;
          bgColor = COLORS.greenTintBg;
          labelBold = true;
        } else {
          dotColor = COLORS.greyDot;
        }
        break;
      case "bad-when-true":
        if (truthy) {
          dotColor = COLORS.terracotta;
          topStripe = COLORS.terracottaStripe;
          bgColor = COLORS.terracottaTintBg;
          labelBold = true;
        } else {
          dotColor = COLORS.greyDot;
        }
        break;
      case "bad-when-false":
        if (!truthy) {
          dotColor = COLORS.terracotta;
          topStripe = COLORS.terracottaStripe;
          bgColor = COLORS.terracottaTintBg;
          labelBold = true;
        } else {
          dotColor = COLORS.green;
          topStripe = COLORS.greenStripe;
          bgColor = COLORS.greenTintBg;
          labelBold = true;
        }
        break;
      case "info":
        if (signal.modeFlag) {
          if (isActiveMode) {
            dotColor = COLORS.green;
            topStripe = COLORS.greenStripe;
            bgColor = COLORS.greenTintBg;
            labelBold = true;
          } else {
            dotColor = COLORS.greyDot;
          }
        } else if (truthy) {
          dotColor = COLORS.blueGreyDot;
          labelBold = true;
        } else {
          dotColor = COLORS.greyDot;
        }
        break;
    }
  }

  const tileBorder = isPlaceholder || isUnknown
    ? `1px dashed ${COLORS.dashedBorder}`
    : `1px solid ${COLORS.border}`;

  const wrapperOpacity = stale ? 0.4 : 1;

  return (
    <div
      style={{
        position: "relative",
        background: bgColor,
        border: tileBorder,
        borderRadius: "4px",
        padding: "12px 14px",
        opacity: wrapperOpacity,
        transition: "opacity 0.3s ease, background 0.3s ease",
        minHeight: "92px",
        display: "flex",
        flexDirection: "column",
        boxSizing: "border-box",
      }}
    >
      {/* top stripe — semantic */}
      {topStripe && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "3px",
            background: topStripe,
            borderTopLeftRadius: "3px",
            borderTopRightRadius: "3px",
          }}
        />
      )}

      {/* stale badge */}
      {stale && (
        <div
          style={{
            position: "absolute",
            top: "6px",
            right: "8px",
            background: COLORS.amber,
            color: COLORS.white,
            fontFamily: FONT_MONO,
            fontSize: "10px",
            fontWeight: 600,
            letterSpacing: "0.05em",
            padding: "2px 6px",
            borderRadius: "2px",
            textTransform: "uppercase",
            opacity: 1, // override the wrapper's reduced opacity for visibility
          }}
        >
          stale
        </div>
      )}

      {/* line 1: symbol + address */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: "8px",
          marginBottom: "8px",
        }}
      >
        <span
          style={{
            fontFamily: FONT_MONO,
            fontSize: "11px",
            fontWeight: 600,
            color: COLORS.primaryText,
            letterSpacing: "0.02em",
            wordBreak: "break-all",
            lineHeight: 1.3,
          }}
        >
          {signal.symbol}
          {signal.inverted && (
            <span
              style={{
                marginLeft: "6px",
                fontWeight: 400,
                color: COLORS.dimText,
                fontSize: "10px",
                textTransform: "lowercase",
              }}
            >
              inv
            </span>
          )}
        </span>
        <span
          style={{
            fontFamily: FONT_MONO,
            fontSize: "10px",
            color: COLORS.dimText,
            textAlign: "right",
            whiteSpace: "nowrap",
            paddingTop: "1px",
          }}
        >
          {signal.address}
          {signal.addressSuffix && (
            <>
              <br />
              <span style={{ fontSize: "10px" }}>{signal.addressSuffix}</span>
            </>
          )}
        </span>
      </div>

      {/* line 2: value */}
      {signal.kind === "number" && !isPlaceholder ? (
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            justifyContent: "flex-end",
            gap: "8px",
            marginBottom: "8px",
            flex: 1,
          }}
        >
          <span
            style={{
              fontFamily: FONT_MONO,
              fontSize: "20px",
              fontWeight: 600,
              color: isUnknown ? COLORS.dimText : COLORS.primaryText,
            }}
          >
            {formatNumber(value)}
          </span>
          {signal.unit && (
            <span
              style={{
                fontFamily: FONT_MONO,
                fontSize: "10px",
                color: COLORS.dimText,
                textTransform: "lowercase",
              }}
            >
              {signal.unit}
            </span>
          )}
        </div>
      ) : (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            marginBottom: "8px",
          }}
        >
          {isPlaceholder ? (
            <span
              style={{
                fontFamily: FONT_SORA,
                fontSize: "16px",
                color: COLORS.dimText,
                fontWeight: 500,
              }}
            >
              {signal.placeholderText ?? "—"}
            </span>
          ) : (
            <>
              <span
                style={{
                  display: "inline-block",
                  width: "10px",
                  height: "10px",
                  borderRadius: "50%",
                  background: isUnknown ? "transparent" : dotColor,
                  border: isUnknown ? `1.5px solid ${COLORS.greyDot}` : "none",
                  flexShrink: 0,
                }}
              />
              <span
                style={{
                  fontFamily: FONT_SORA,
                  fontSize: "15px",
                  fontWeight: labelBold ? 600 : 500,
                  color: isUnknown ? COLORS.dimText : COLORS.primaryText,
                }}
              >
                {isUnknown ? "—" : formatBoolValue(value)}
              </span>
            </>
          )}
        </div>
      )}

      {/* line 3: bilingual label */}
      <div
        style={{
          fontFamily: FONT_INTER,
          fontSize: "11px",
          color: COLORS.dimText,
          lineHeight: 1.4,
          marginTop: "auto",
        }}
      >
        {isPlaceholder ? signal.placeholderHint ?? signal.label : signal.label}
      </div>
    </div>
  );
};

export default SignalTile;
